import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Megaphone, Plus, Trash2, Save, Eye, Users, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  listAnnouncements,
  upsertAnnouncement,
  deleteAnnouncement,
  type Announcement,
  listAudienceGroups,
  createAudienceGroup,
  deleteAudienceGroup,
  listGroupMembers,
  setGroupMembers,
  listAllUsers,
  type AudienceGroup,
  type UserOption,
} from "@/lib/announcements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { BannerView, PopupView } from "@/components/AnnouncementDisplay";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app/staff/announcements")({ component: AnnouncementsPage });

type Draft = Partial<Announcement> & { id?: string };

function AnnouncementsPage() {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["announcements", "all"],
    queryFn: listAnnouncements,
  });
  const [editing, setEditing] = useState<Draft | null>(null);
  const [audienceFilter, setAudienceFilter] = useState<"all" | "customer" | "staff" | "everyone">(
    "all",
  );
  const [groupFilter, setGroupFilter] = useState<string>("");
  const [groupsOpen, setGroupsOpen] = useState(false);

  const { data: groups = [] } = useQuery({
    queryKey: ["audience-groups"],
    queryFn: listAudienceGroups,
  });
  const { data: users = [] } = useQuery({ queryKey: ["all-users"], queryFn: listAllUsers });

  const saveMut = useMutation({
    mutationFn: (d: Draft) => upsertAnnouncement(d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["announcements"] });
      toast.success("Saved");
      setEditing(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteAnnouncement(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["announcements"] });
      toast.success("Deleted");
    },
  });

  const filtered = items.filter((a) => {
    if (groupFilter && !(a.audience_group_ids ?? []).includes(groupFilter)) return false;
    if (audienceFilter === "all") return true;
    if (audienceFilter === "everyone") return a.audience === "all";
    return a.audience === audienceFilter;
  });
  const counts = {
    all: items.length,
    customer: items.filter((a) => a.audience === "customer").length,
    staff: items.filter((a) => a.audience === "staff").length,
    everyone: items.filter((a) => a.audience === "all").length,
  };

  return (
    <div className="space-y-6 px-4 sm:px-0 pb-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Megaphone className="h-5 w-5 text-primary" />
          <h1 className="text-xl sm:text-2xl font-bold truncate">Announcements</h1>
        </div>
        <div className="flex items-center gap-2 sm:flex-none">
          <Button
            variant="outline"
            onClick={() => setGroupsOpen(true)}
            className="flex-1 sm:flex-none"
          >
            <Users className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Manage groups</span>
            <span className="sm:hidden">Groups</span>
          </Button>
          <Button
            onClick={() =>
              setEditing({ mode: "banner", enabled: true, dismissible: true, audience: "customer" })
            }
            className="flex-1 sm:flex-none"
          >
            <Plus className="h-4 w-4 mr-1" /> New
          </Button>
        </div>
      </div>

      <div className="-mx-4 sm:mx-0 overflow-x-auto sm:overflow-visible">
        <div className="flex items-center gap-2 px-4 sm:px-0 sm:flex-wrap min-w-max sm:min-w-0">
          {(
            [
              { key: "all", label: "All" },
              { key: "customer", label: "Customers only" },
              { key: "staff", label: "Staff only" },
              { key: "everyone", label: "Everyone" },
            ] as const
          ).map((opt) => {
            const active = audienceFilter === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setAudienceFilter(opt.key)}
                className={
                  "shrink-0 rounded-full px-3 py-1 text-xs font-medium border transition-colors " +
                  (active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:text-foreground")
                }
                aria-pressed={active}
              >
                {opt.label}
                <span className={"ml-1.5 " + (active ? "opacity-80" : "opacity-60")}>
                  {counts[opt.key]}
                </span>
              </button>
            );
          })}
          {groups.length > 0 && (
            <div className="ml-2 inline-flex items-center gap-2 shrink-0">
              <span className="text-xs text-muted-foreground">Group:</span>
              <Select
                value={groupFilter || "__none"}
                onValueChange={(v) => setGroupFilter(v === "__none" ? "" : v)}
              >
                <SelectTrigger className="h-7 w-40 sm:w-48 text-xs">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Any group</SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {groupFilter && (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setGroupFilter("")}
                >
                  <X className="h-3 w-3 inline" /> clear
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-8 text-center text-muted-foreground">
          {items.length === 0
            ? "No announcements yet. Create one to show a banner or popup."
            : "No announcements match this filter."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((a) => (
            <div
              key={a.id}
              className="rounded-2xl border bg-card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${a.enabled ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}
                  >
                    {a.enabled ? "Live" : "Off"}
                  </span>
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    {a.mode}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {a.audience === "all"
                      ? "Everyone"
                      : a.audience === "staff"
                        ? "Staff"
                        : "Customers"}
                  </span>
                </div>
                <div className="mt-1 font-medium truncate">{a.title || "(untitled)"}</div>
                <div className="text-sm text-muted-foreground line-clamp-2 sm:truncate">
                  {a.body}
                </div>
              </div>
              <div className="flex gap-2 shrink-0 self-end sm:self-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(a)}
                  className="flex-1 sm:flex-none"
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (confirm("Delete this announcement?")) delMut.mutate(a.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Editor
          draft={editing}
          onChange={setEditing}
          onSave={() => saveMut.mutate(editing)}
          onCancel={() => setEditing(null)}
          saving={saveMut.isPending}
          groups={groups}
          users={users}
        />
      )}

      <ManageGroupsDialog
        open={groupsOpen}
        onOpenChange={setGroupsOpen}
        groups={groups}
        users={users}
      />
    </div>
  );
}

function Editor({
  draft,
  onChange,
  onSave,
  onCancel,
  saving,
  groups,
  users,
}: {
  draft: Draft;
  onChange: (d: Draft) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  groups: AudienceGroup[];
  users: UserOption[];
}) {
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => onChange({ ...draft, [k]: v });
  const toLocal = (s?: string | null) => (s ? new Date(s).toISOString().slice(0, 16) : "");
  const fromLocal = (s: string) => (s ? new Date(s).toISOString() : null);
  const [previewPopupOpen, setPreviewPopupOpen] = useState(false);
  const previewAnnouncement = {
    id: draft.id || "preview",
    title: draft.title || "Preview title",
    body: draft.body || "",
    mode: (draft.mode || "banner") as Announcement["mode"],
    enabled: true,
    bg_color: draft.bg_color || null,
    text_color: draft.text_color || null,
    cta_label: draft.cta_label || null,
    cta_url: draft.cta_url || null,
    dismissible: draft.dismissible !== false,
    starts_at: draft.starts_at || null,
    ends_at: draft.ends_at || null,
    audience: (draft.audience || "customer") as Announcement["audience"],
    audience_group_ids: draft.audience_group_ids ?? [],
    audience_user_ids: draft.audience_user_ids ?? [],
  } as Announcement;

  const status = scheduleStatus(draft);

  return (
    <div className="rounded-2xl border bg-card p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base sm:text-lg font-semibold">
          {draft.id ? "Edit" : "New"} announcement
        </h2>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-xs px-2 py-0.5 rounded-full border",
              status.tone === "live" && "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
              status.tone === "scheduled" && "bg-amber-500/10 text-amber-700 border-amber-500/30",
              status.tone === "ended" && "bg-muted text-muted-foreground border-border",
              status.tone === "off" && "bg-muted text-muted-foreground border-border",
            )}
            title={status.detail}
          >
            {status.label}
          </span>
        </div>
      </div>

      {/* Inline preview */}
      <div className="rounded-xl border bg-background overflow-hidden">
        <div className="flex items-center justify-between px-3 py-1.5 border-b bg-muted/40 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Eye className="h-3.5 w-3.5" /> Live preview ({draft.mode || "banner"})
          </span>
          {(draft.mode || "banner") === "popup" && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={() => setPreviewPopupOpen(true)}
            >
              Open popup
            </Button>
          )}
        </div>
        {(draft.mode || "banner") === "banner" ? (
          <BannerView
            a={previewAnnouncement}
            onDismiss={() => {
              /* preview dismiss is a no-op */
            }}
          />
        ) : (
          <div className="px-4 py-3 text-sm text-muted-foreground">
            Click <em>Open popup</em> to preview the modal.
          </div>
        )}
      </div>
      {previewPopupOpen && (draft.mode || "banner") === "popup" && (
        <PopupView a={previewAnnouncement} onDismiss={() => setPreviewPopupOpen(false)} />
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Mode</Label>
          <Select
            value={draft.mode || "banner"}
            onValueChange={(v) => set("mode", v as Announcement["mode"])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="banner">Banner</SelectItem>
              <SelectItem value="popup">Popup</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 md:pt-6">
          <div className="flex items-center gap-3">
            <Switch checked={!!draft.enabled} onCheckedChange={(v) => set("enabled", v)} />
            <Label>Enabled</Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={draft.dismissible !== false}
              onCheckedChange={(v) => set("dismissible", v)}
            />
            <Label>Dismissible</Label>
          </div>
        </div>
        <div className="md:col-span-2">
          <Label>Title</Label>
          <Input value={draft.title || ""} onChange={(e) => set("title", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <Label>Body</Label>
          <Textarea
            rows={3}
            value={draft.body || ""}
            onChange={(e) => set("body", e.target.value)}
          />
        </div>
        <div>
          <Label>Audience</Label>
          <Select
            value={(draft.audience as string) || "customer"}
            onValueChange={(v) => set("audience", v as Announcement["audience"])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="customer">Customers only</SelectItem>
              <SelectItem value="staff">Staff only</SelectItem>
              <SelectItem value="all">Everyone (customers + staff)</SelectItem>
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">
            Anyone in the groups or users below also sees it, regardless of audience.
          </p>
        </div>
        <div>
          <Label>CTA label</Label>
          <Input
            value={draft.cta_label || ""}
            onChange={(e) => set("cta_label", e.target.value)}
            placeholder="Learn more"
          />
        </div>
        <div>
          <Label>CTA URL</Label>
          <Input
            value={draft.cta_url || ""}
            onChange={(e) => set("cta_url", e.target.value)}
            placeholder="/app/customer/shop"
          />
        </div>
        <div>
          <Label>Background colour</Label>
          <Input
            type="color"
            value={draft.bg_color || "#000000"}
            onChange={(e) => set("bg_color", e.target.value)}
          />
        </div>
        <div>
          <Label>Text colour</Label>
          <Input
            type="color"
            value={draft.text_color || "#ffffff"}
            onChange={(e) => set("text_color", e.target.value)}
          />
        </div>
        <div>
          <Label>Starts at</Label>
          <Input
            type="datetime-local"
            value={toLocal(draft.starts_at)}
            onChange={(e) => set("starts_at", fromLocal(e.target.value))}
          />
        </div>
        <div>
          <Label>Ends at</Label>
          <Input
            type="datetime-local"
            value={toLocal(draft.ends_at)}
            onChange={(e) => set("ends_at", fromLocal(e.target.value))}
          />
        </div>

        <div className="md:col-span-2">
          <Label>Also show to specific groups</Label>
          <MultiPicker
            empty="No groups defined yet — create some via Manage groups."
            options={groups.map((g) => ({
              value: g.id,
              label: `${g.name}${typeof g.member_count === "number" ? ` · ${g.member_count}` : ""}`,
            }))}
            value={draft.audience_group_ids ?? []}
            onChange={(v) => set("audience_group_ids", v)}
          />
        </div>
        <div className="md:col-span-2">
          <Label>Also show to specific users</Label>
          <MultiPicker
            empty="No users found."
            options={users.map((u) => ({
              value: u.id,
              label: `${u.full_name || u.email || u.id.slice(0, 8)} · ${u.role}`,
            }))}
            value={draft.audience_user_ids ?? []}
            onChange={(v) => set("audience_user_ids", v)}
            searchable
          />
        </div>
      </div>
      <AudiencePreview draft={draft} groups={groups} users={users} />
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
        <Button variant="outline" onClick={onCancel} className="w-full sm:w-auto">
          Cancel
        </Button>
        {(draft.mode || "banner") === "popup" && (
          <Button
            variant="outline"
            onClick={() => setPreviewPopupOpen(true)}
            className="w-full sm:w-auto"
          >
            <Eye className="h-4 w-4 mr-1" /> Preview popup
          </Button>
        )}
        <Button onClick={onSave} disabled={saving} className="w-full sm:w-auto">
          <Save className="h-4 w-4 mr-1" />
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function scheduleStatus(d: Draft): {
  label: string;
  tone: "live" | "scheduled" | "ended" | "off";
  detail: string;
} {
  if (!d.enabled) return { label: "Disabled", tone: "off", detail: "Toggle 'Enabled' to publish." };
  const now = Date.now();
  const start = d.starts_at ? new Date(d.starts_at).getTime() : null;
  const end = d.ends_at ? new Date(d.ends_at).getTime() : null;
  if (end && now > end)
    return { label: "Ended", tone: "ended", detail: `Ended ${new Date(end).toLocaleString()}` };
  if (start && now < start)
    return {
      label: `Scheduled · ${new Date(start).toLocaleString()}`,
      tone: "scheduled",
      detail: "Will go live at start time.",
    };
  return {
    label: "Live now",
    tone: "live",
    detail: end ? `Until ${new Date(end).toLocaleString()}` : "No end date.",
  };
}

function AudiencePreview({
  draft,
  groups,
  users,
}: {
  draft: Draft;
  groups: AudienceGroup[];
  users: UserOption[];
}) {
  const audience = (draft.audience || "customer") as Announcement["audience"];
  const groupIds = draft.audience_group_ids ?? [];
  const userIds = draft.audience_user_ids ?? [];

  const groupMemberQueries = useQueries({
    queries: groupIds.map((gid) => ({
      queryKey: ["audience-group-members", gid],
      queryFn: () => listGroupMembers(gid),
      staleTime: 60_000,
    })),
  });
  const groupsLoading = groupMemberQueries.some((q) => q.isLoading);

  // Base audience set from profiles
  const baseIds = new Set<string>();
  for (const u of users) {
    if (audience === "all" || u.role === audience || (audience === "staff" && u.role === "admin")) {
      baseIds.add(u.id);
    }
  }
  const baseCount = baseIds.size;

  const groupRows = groupIds.map((gid, i) => {
    const g = groups.find((x) => x.id === gid);
    const members = (groupMemberQueries[i]?.data as string[] | undefined) ?? [];
    const extra = members.filter((m) => !baseIds.has(m)).length;
    return { id: gid, name: g?.name || "Group", total: members.length, extra };
  });

  const userRows = userIds.map((uid) => {
    const u = users.find((x) => x.id === uid);
    const inBase = baseIds.has(uid);
    return {
      id: uid,
      label: u ? u.full_name || u.email || uid.slice(0, 8) : uid.slice(0, 8),
      role: u?.role,
      inBase,
    };
  });

  // Compute deduped union total
  const union = new Set(baseIds);
  groupMemberQueries.forEach((q) => {
    (q.data as string[] | undefined)?.forEach((id) => union.add(id));
  });
  userIds.forEach((id) => union.add(id));
  const totalUnique = union.size;

  const audienceLabel =
    audience === "all"
      ? "Everyone (customers + staff)"
      : audience === "staff"
        ? "All staff"
        : "All customers";

  return (
    <div className="rounded-xl border bg-muted/30 p-3 sm:p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex items-center gap-2 text-sm font-medium">
          <Users className="h-4 w-4 text-primary" />
          Audience preview
        </div>
        <div className="text-xs text-muted-foreground">
          {groupsLoading ? (
            "Calculating…"
          ) : (
            <>
              Will reach <span className="font-semibold text-foreground">{totalUnique}</span>{" "}
              {totalUnique === 1 ? "person" : "people"}
            </>
          )}
        </div>
      </div>

      <div className="text-xs space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground">{audienceLabel}</span>
          <span className="tabular-nums font-medium">{baseCount}</span>
        </div>

        {groupRows.length > 0 && (
          <div className="pt-1 border-t border-border/60">
            <div className="text-muted-foreground mb-1">Groups ({groupRows.length})</div>
            <ul className="space-y-1">
              {groupRows.map((g) => (
                <li key={g.id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{g.name}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {g.total}{" "}
                    {g.extra > 0 && g.extra !== g.total && (
                      <span className="text-emerald-600">(+{g.extra} new)</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {userRows.length > 0 && (
          <div className="pt-1 border-t border-border/60">
            <div className="text-muted-foreground mb-1">Specific users ({userRows.length})</div>
            <ul className="flex flex-wrap gap-1.5">
              {userRows.map((u) => (
                <li
                  key={u.id}
                  className={cn(
                    "rounded-full border px-2 py-0.5",
                    u.inBase
                      ? "bg-muted text-muted-foreground border-border"
                      : "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
                  )}
                  title={u.inBase ? "Already included via audience" : "Added on top of audience"}
                >
                  {u.label}
                  {u.role && <span className="ml-1 opacity-60">· {u.role}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function MultiPicker({
  options,
  value,
  onChange,
  empty,
  searchable,
}: {
  options: { value: string; label: string }[];
  value: string[];
  onChange: (v: string[]) => void;
  empty?: string;
  searchable?: boolean;
}) {
  const [q, setQ] = useState("");
  const set = new Set(value);
  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : options;
  const toggle = (v: string) => {
    const next = new Set(value);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(Array.from(next));
  };
  if (options.length === 0) {
    return <p className="text-xs text-muted-foreground py-2">{empty}</p>;
  }
  return (
    <div className="space-y-2">
      {searchable && (
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="pl-7 h-8 text-sm"
          />
        </div>
      )}
      <div className="max-h-48 overflow-y-auto rounded-lg border bg-background p-1">
        {filtered.map((o) => {
          const checked = set.has(o.value);
          return (
            <label
              key={o.value}
              className={cn(
                "flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm",
                checked ? "bg-primary/10" : "hover:bg-muted",
              )}
            >
              <input
                type="checkbox"
                className="rounded"
                checked={checked}
                onChange={() => toggle(o.value)}
              />
              <span className="truncate">{o.label}</span>
            </label>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-xs text-muted-foreground py-2 text-center">No matches</div>
        )}
      </div>
      {value.length > 0 && (
        <div className="text-xs text-muted-foreground">{value.length} selected</div>
      )}
    </div>
  );
}

function ManageGroupsDialog({
  open,
  onOpenChange,
  groups,
  users,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  groups: AudienceGroup[];
  users: UserOption[];
}) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [editingGroup, setEditingGroup] = useState<AudienceGroup | null>(null);

  const createMut = useMutation({
    mutationFn: (input: { name: string; description: string }) =>
      createAudienceGroup(input.name.trim(), input.description.trim() || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["audience-groups"] });
      setNewName("");
      setNewDesc("");
      toast.success("Group created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: deleteAudienceGroup,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["audience-groups"] });
      toast.success("Group deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Audience groups</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          <div className="rounded-xl border p-3 space-y-2">
            <div className="text-sm font-medium">Create group</div>
            <div className="grid gap-2 md:grid-cols-2">
              <Input
                placeholder="Name (e.g. Beta testers)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <Input
                placeholder="Description (optional)"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                disabled={!newName.trim() || createMut.isPending}
                onClick={() => createMut.mutate({ name: newName, description: newDesc })}
              >
                <Plus className="h-4 w-4 mr-1" /> Create
              </Button>
            </div>
          </div>

          {groups.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No groups yet.</p>
          ) : (
            <ul className="space-y-2">
              {groups.map((g) => (
                <li key={g.id} className="rounded-xl border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{g.name}</div>
                      {g.description && (
                        <div className="text-xs text-muted-foreground truncate">
                          {g.description}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        {g.member_count ?? 0} members
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => setEditingGroup(g)}>
                        Members
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Delete group "${g.name}"?`)) delMut.mutate(g.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
      {editingGroup && (
        <GroupMembersDialog
          group={editingGroup}
          users={users}
          onClose={() => setEditingGroup(null)}
        />
      )}
    </Dialog>
  );
}

function GroupMembersDialog({
  group,
  users,
  onClose,
}: {
  group: AudienceGroup;
  users: UserOption[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: initial = [] } = useQuery({
    queryKey: ["group-members", group.id],
    queryFn: () => listGroupMembers(group.id),
  });
  const [selected, setSelected] = useState<string[]>(initial);
  useEffect(() => {
    setSelected(initial);
  }, [initial]);
  const saveMut = useMutation({
    mutationFn: () => setGroupMembers(group.id, selected),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["group-members", group.id] });
      qc.invalidateQueries({ queryKey: ["audience-groups"] });
      toast.success("Members updated");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Members of "{group.name}"</DialogTitle>
        </DialogHeader>
        <MultiPicker
          empty="No users found."
          searchable
          options={users.map((u) => ({
            value: u.id,
            label: `${u.full_name || u.email || u.id.slice(0, 8)} · ${u.role}`,
          }))}
          value={selected}
          onChange={setSelected}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            <Save className="h-4 w-4 mr-1" /> {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
